import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, SourceBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, MessageCircle, Clock, Users, CalendarDays, Bell, UserCheck, UserX } from "lucide-react";

const fmtDate = (d) => new Date(d).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export default function Agenda() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations", { params: { date } });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}/status`, { status });
      const msg = { confermata: "Confermata — WhatsApp inviato ✅", completata: "Segnata completata", no_show: "Segnata no-show", cancellata: "Annullata — cliente avvisato" };
      toast.success(msg[status] || "Aggiornata");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const notify = async (id) => {
    try { await api.post(`/reservations/${id}/notify`, null, { params: { kind: "promemoria" } }); toast.success("Promemoria WhatsApp inviato ✅"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const grouped = useMemo(() => {
    const active = items.filter((i) => i.status !== "cancellata");
    return active.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  }, [items]);

  const counts = useMemo(() => ({
    total: items.length,
    confermate: items.filter((i) => i.status === "confermata").length,
    richieste: items.filter((i) => i.status === "richiesta").length,
    coperti: items.filter((i) => ["confermata", "completata"].includes(i.status)).reduce((s, i) => s + i.party_size, 0),
  }), [items]);

  return (
    <div className="space-y-5" data-testid="agenda-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-primary">Agenda</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 capitalize">{fmtDate(date)}</h1>
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full sm:w-48" data-testid="agenda-date-input" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Prenotazioni", value: counts.total, icon: CalendarDays },
          { label: "Confermate", value: counts.confermate, icon: Check },
          { label: "Da confermare", value: counts.richieste, icon: Clock },
          { label: "Coperti/Ospiti", value: counts.coperti, icon: Users },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`agenda-stat-${s.label}`}>
            <div className="flex items-center justify-between"><span className="text-xs text-slate-500 font-medium">{s.label}</span><s.icon className="h-4 w-4 text-slate-400" /></div>
            <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-400 py-12 text-center">Caricamento...</p>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Nessuna prenotazione per questa data.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid={`agenda-reservation-${r.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 sm:w-40 shrink-0">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary font-heading font-bold">
                    {r.time || "—"}
                  </div>
                  <div className="sm:hidden"><StatusBadge status={r.status} /></div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900">{r.customer_name}</p>
                    <SourceBadge source={r.source} />
                  </div>
                  <p className="text-sm text-slate-500 flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {r.party_size}</span>
                    <span>{r.customer_phone}</span>
                    {r.notifications?.some((n) => n.channel === "whatsapp") && <span className="flex items-center gap-1 text-emerald-600"><MessageCircle className="h-3.5 w-3.5" /> inviato</span>}
                  </p>
                  {r.notes && <p className="text-xs text-slate-400 mt-1 italic">"{r.notes}"</p>}
                </div>
                <div className="hidden sm:block"><StatusBadge status={r.status} /></div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                {r.status === "richiesta" && (
                  <>
                    <Button size="sm" onClick={() => changeStatus(r.id, "confermata")} className="rounded-lg" data-testid={`agenda-confirm-${r.id}`}><Check className="h-4 w-4 mr-1" /> Conferma</Button>
                    <Button size="sm" variant="outline" onClick={() => changeStatus(r.id, "cancellata")} className="rounded-lg text-destructive" data-testid={`agenda-reject-${r.id}`}><X className="h-4 w-4 mr-1" /> Rifiuta</Button>
                  </>
                )}
                {r.status === "confermata" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => changeStatus(r.id, "completata")} className="rounded-lg" data-testid={`agenda-complete-${r.id}`}><UserCheck className="h-4 w-4 mr-1" /> Presente</Button>
                    <Button size="sm" variant="outline" onClick={() => changeStatus(r.id, "no_show")} className="rounded-lg text-amber-700" data-testid={`agenda-noshow-${r.id}`}><UserX className="h-4 w-4 mr-1" /> No-show</Button>
                    <Button size="sm" variant="ghost" onClick={() => notify(r.id)} className="rounded-lg text-emerald-600" data-testid={`agenda-remind-${r.id}`}><Bell className="h-4 w-4 mr-1" /> Promemoria</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
